import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTheme, Flex, Box, HStack, AvatarGroup} from '@chakra-ui/react';
import ConvinceMeToGoButton from '../../../pages/Event/ConvinceMeToGoButton';
import {TextA12, TextG10, TextG12, TextG14} from '../../typography';
import {Event} from '../../../types/article';
import {generatePath, useLocation, useNavigate} from 'react-router-dom';
import {ROUTES} from '../../../constants/routes';
import DefaultEventImage from '../../../assets/images/default_event.png';
import {getEventDate} from '../../../utils/date';
import useGetUserLocation from '../../../hooks/useUserLocation';
import {useTrackVisibility} from 'react-intersection-observer-hook';
import useVisibleArticles from '../../../hooks/useVisible';
import {getDisplayPrice} from '../../../utils/price';
import DotSeparator from '../../DotSeparator';
import Icon from '../../Icon';
import {useOffers} from '../../../hooks/useOffers';
import {useFetchEventRSVPStats, useFetchEventInterestedGoingUsers} from '../../../hooks/useEvent';
import {calculateDistance, getDistanceString} from '../../../utils/distance';
import PremiumCard from '../../Card/PremiumCard';
import {storeScrollPositionInSession} from '../../../utils/scroll';
import CardBadge from '../../CardBadge';
import RSVPController from '../../../pages/Event/RSVP/RSVPController';
import useAuth from '../../../hooks/useAuth';
import {BlueButton} from '../../button';
import UseablePassPill from './UseablePassPill';
import {useFetchUserTickets} from '../../../hooks/useUserTickets';
import {filter, has} from 'lodash';
import NotWalkaboutAffiliatedPill from './NotWalkaboutAffiliatedPill';
import InterestedToggle from '../../InterestedToggle';
import PointBoostBadge from '../PlaceCard/PointBoostBadge';
import {Link as DomLink} from 'react-router-dom';
import {UsersListModal} from '../../UsersList';
import UserAvatar from '../../UserAvatar';

const iconStyle = {
  color: 'grey',
  fontSize: '12px',
  marginRight: '5px',
};

const optionStyle: React.CSSProperties = {
  fontSize: '18px',
  paddingTop: '3px',
};

// Ambassador topic ID - when this topic is present, show the "Meet a Community Connector" badge
const AMBASSADOR_PRESENT_TOPIC = process.env.NODE_ENV === 'production' ? 336 : 223;

const PremiumEventCard = ({
  event,
  leftTopItem,
  rightTopItem,
  height,
  modal,
  hideRSVP,
  isFeatured = false,
  isSlide,
  onClick,
  isPreview,
  hideInterestToggle = false,
}: {
  event?: Event;
  leftTopItem?: any;
  rightTopItem?: any;
  height?: string;
  modal?: boolean;
  hideRSVP?: boolean;
  isFeatured?: boolean;
  isSlide?: boolean;
  onClick?: (e) => void;
  isPreview?: boolean;
  hideInterestToggle?: boolean;
}) => {
  if (!event) return null; // TODO: revisit the loading flow to prevent this check
  const theme = useTheme();
  let userLocation;
  if (!isPreview) {
    userLocation = useGetUserLocation();
  }
  const cardChildContent = useRef<HTMLDivElement>(null);
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const [ref, {wasEverVisible, isVisible}] = useTrackVisibility();
  const {addVisibleArticle} = useVisibleArticles();
  const {user} = useAuth();
  const eventDiscount = user?.activeMembershipPlan?.event_discount;

  const fetchTickets = useFetchUserTickets(!!user);
  const tickets = fetchTickets.data?.data;
  const eventTickets = filter(tickets, (ticket) => ticket.event_id == event.id);
  const userHasTickets = eventTickets && eventTickets.length > 0;

  const fetchOffers = useOffers({
    articleType: 'eventId',
    articleId: event.id.toString(),
    enabled: isVisible && !!user,
  });

  const {data: interestedData} = useFetchEventInterestedGoingUsers(event.id);
  const usersInterested = interestedData?.pages[0]?.data?.total || 0;
  const interestedUsers = interestedData?.pages[0]?.data?.data || [];
  const [showUsersModal, setShowUsersModal] = useState(false);

  const isWalkaboutEvent = Boolean(event.is_walkabout_official);

  useEffect(() => {
    if (wasEverVisible && event?.id.toString()) {
      addVisibleArticle({id: event.id.toString(), type: 'event'});
    }
  }, [wasEverVisible]);

  const eventDate = useMemo(
    () =>
      !!event.is_recurring && event.recurring_schedule
        ? event.recurring_schedule
        : getEventDate(event.start_date_time, event.end_date_time),
    [event],
  );

  // Check if this event has the ambassador topic
  const hasAmbassadorTopic = event?.topics_of_interest?.some(
    (topic) => topic.id === AMBASSADOR_PRESENT_TOPIC,
  );

  return (
    <Box position="relative" width="100%" pt={hasAmbassadorTopic ? '32px' : 0}>
      {hasAmbassadorTopic && (
        <Box
          position="absolute"
          top="-4px"
          left="16px"
          backgroundColor="#FAE7F0"
          borderRadius="8px 8px 0 0"
          px="8px"
          py="6px"
          zIndex={10}>
          <TextG12 fontWeight="500" color="#C9136B" lineHeight="18px">
            👋 Meet a Community Connector
          </TextG12>
        </Box>
      )}
      <PremiumCard
        outline={
          hasAmbassadorTopic
            ? '2px solid #E86EAA'
            : event.is_walkabout_official
              ? '1px solid #FFC0C6'
              : 'none'
        }
        article={event}
        isSlide={isSlide}
        onClick={(e) => {
          if (onClick) {
            onClick(e);
            return;
          }
          if (
            e?.target.tagName === 'BUTTON' ||
            e?.target.parentNode.tagName === 'BUTTON' ||
            e?.target.parentNode.parentNode.tagName === 'BUTTON'
          )
            return;

          const route = modal
            ? {
                pathname,
                search: `?id=${event.id.toString()}&articleType=${
                  event.type ? event.type.toLowerCase() : 'event'
                }`,
              }
            : generatePath(ROUTES.EVENT, {
                eventId: event.id.toString(),
              });
          storeScrollPositionInSession();
          navigate(route);
        }}
        overlayBackgroundColor={isFeatured ? '#2729295C' : ''}
        overlayHeight={
          isFeatured && cardChildContent?.current?.clientHeight
            ? `${cardChildContent.current.clientHeight + 24}px`
            : ''
        }
        borderRadius={isFeatured ? '0px' : '12px'}
        childPaddingX={4}
        height={height}
        coverImage={event.image_url || DefaultEventImage}
        leftTopItem={leftTopItem}
        rightTopItem={
          hasAmbassadorTopic && rightTopItem ? (
            <Box position="absolute" right="12px" top="46px" zIndex={10}>
              {React.cloneElement(rightTopItem, {
                position: 'relative',
                right: 'unset',
                top: 'unset',
              })}
            </Box>
          ) : (
            rightTopItem
          )
        }>
        <Box ref={cardChildContent}>
          <Flex gap={2} flexWrap="wrap">
            {event.is_walkabout_official && (
              <CardBadge label="WALKABOUT OFFICIAL" w="145px" top="-12px" />
            )}
            {fetchOffers?.offers && fetchOffers.offers.length > 0 && (
              <CardBadge
                label={`🔥 ${fetchOffers.offers.length} ${fetchOffers.offers.length === 1 ? 'OFFER' : 'OFFERS'}`}
                top="-12px"
              />
            )}
          </Flex>
          <Flex justifyContent="space-between" py={1} alignItems="center" textAlign="center">
            <Flex
              mt={event.is_walkabout_official ? '-4px' : 0}
              ref={ref}
              alignItems="center"
              textAlign="center"
              color={theme.colors.grey}>
              {/* {!!event.is_recurring && <Icon iconName="fi-rr-time-past" style={iconStyle} />} */}
              <TextG10>{!!event.is_recurring ? `${eventDate} (Recurring)` : eventDate}</TextG10>
              {/* {fetchOffers && fetchOffers.offers && fetchOffers.offers.length > 0 && (
                <>
                  <DotSeparator color={theme.colors.grey} />
                  <TextG10 py={1} color={theme.colors.rose}>
                    {`${fetchOffers.offers.length} ${
                      fetchOffers.offers.length == 1 ? 'Offer' : 'Offers'
                    }`}
                  </TextG10>
                </>
              )} */}
              {/* {event.price && (
                <>
                  <DotSeparator color={theme.colors.grey} />
                <TextG10>
                  {getDisplayPrice(event.price, event?.max_price, event?.price_override)}
                </TextG10>
                  {event.latitude && event.longitude && userLocation?.data && (
                    <>
                      <DotSeparator color={theme.colors.grey} />
                      <TextG10 sx={{wordWrap: 'normal !important'}}>
                        {getDistanceString(
                          calculateDistance(
                            {
                              latitude: userLocation?.data?.latitude,
                              longitude: userLocation?.data?.longitude,
                            },
                            {latitude: Number(event.latitude), longitude: Number(event.longitude)}
                          )
                        )}
                      </TextG10>
                    </>
                  )}
                </>
              )} */}
            </Flex>
            <Flex gap="5px">
              <TextG12
                fontSize={event.has_tickets && eventDiscount && eventDiscount > 0 ? '10px' : '12px'}
                color={theme.colors.lightBlack}
                as={
                  event.price > 0 &&
                  !event.price_override &&
                  event.has_tickets &&
                  eventDiscount &&
                  eventDiscount > 0 &&
                  !event.price_override
                    ? 's'
                    : ''
                }>
                {getDisplayPrice(event.price, event?.max_price, event?.price_override)}
              </TextG12>
              {event.price > 0 &&
                event.has_tickets &&
                eventDiscount &&
                eventDiscount > 0 &&
                !event.price_override && (
                  <TextG12 color={theme.colors.lightBlack}>
                    {getDisplayPrice(event.price - (event.price * eventDiscount) / 100)}
                  </TextG12>
                )}
            </Flex>
          </Flex>
          <Flex mb={1} alignItems={'bottom'} width={'100%'} justifyContent={'space-between'}>
            <TextG14
              isTruncated="&hellip;"
              noOfLines={2}
              fontWeight={700}
              color={theme.colors.black}>
              {event.title}
            </TextG14>
          </Flex>
          {event.place && (
            <HStack gap={0}>
              <DomLink
                style={{
                  color: '#276ACD',
                  fontWeight: '500',
                  fontFamily: 'Gordita',
                  fontSize: '12px',
                }}
                to={generatePath(ROUTES.PLACE, {
                  placeId: event.place.id.toString(),
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  storeScrollPositionInSession();
                }}>
                <TextG10>{event.place.name}</TextG10>
              </DomLink>

              {userLocation?.data?.latitude &&
                userLocation?.data.longitude &&
                event.place?.latitude &&
                event.place?.longitude && (
                  <>
                    <DotSeparator color="black" marginTop="0.25rem" />
                    <TextG10 noOfLines={1} minWidth="90px" ml="0 !important" mt=".25rem !important">
                      {getDistanceString(
                        calculateDistance(
                          {latitude: event.place?.latitude, longitude: event.place?.longitude},
                          userLocation.data,
                        ),
                      )}{' '}
                      away
                    </TextG10>
                  </>
                )}
            </HStack>
          )}
          {!isPreview && <UseablePassPill event={event} />}
          <PointBoostBadge article={event} />
          {!isWalkaboutEvent && <NotWalkaboutAffiliatedPill />}
          <Flex my={2} alignItems="center" justifyContent="flex-start" gap="0.3rem">
            {!onClick && event.has_tickets && (
              <BlueButton
                w="120px"
                height="30px"
                onClick={() =>
                  userHasTickets
                    ? navigate(`/tickets/${event.id}`)
                    : navigate(`/event/${event.id}/tickets`, {
                        replace: true,
                      })
                }>
                <TextG10 fontWeight="500">{userHasTickets ? 'See Tickets' : 'Get Tickets'}</TextG10>
              </BlueButton>
            )}
            {isWalkaboutEvent && !onClick && (
              <ConvinceMeToGoButton
                event={event}
                textColor={
                  event.has_tickets || event.rsvp_status?.status !== null ? 'black' : 'white'
                }
                background={event.has_tickets ? 'white' : '#276ACD'}
                typographySize="sm"
                buttonProps={{
                  flexGrow: 0.3,
                  height: '2rem',
                  border: event.has_tickets ? '1px solid black' : '1px solid white',
                  maxW: '100%',
                }}
              />
            )}
            {isWalkaboutEvent && !onClick && !hideRSVP && !event.has_tickets && (
              <RSVPController
                event={event}
                buttonProps={{
                  flexGrow: 0.2,
                  height: '2rem',
                  border: '1px solid white',
                  maxW: '50%',
                }}
                variant="sm"
              />
            )}
            {!isWalkaboutEvent && !isPreview && !hideInterestToggle && (
              <Box mt={1}>
                <InterestedToggle
                  event={event}
                  width="fit-content"
                  height="20px"
                  fontSize="12px"
                  switchSize="sm"
                  switchLocation="left"
                  hasBorder={false}
                />
              </Box>
            )}
            {usersInterested > 0 && (
              <Flex
                align="center"
                gap={2}
                cursor="pointer"
                _hover={{opacity: 0.8}}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowUsersModal(true);
                }}>
                <AvatarGroup size="xs" max={3} spacing={-2}>
                  {interestedUsers.slice(0, 3).map((user: any, index: number) => (
                    <UserAvatar
                      key={user.user_id || `user-${index}`}
                      user={
                        {
                          id: user.user_id,
                          name: user.name,
                          surname: user.surname,
                          handle: user.handle,
                          profilePhotoUrl: user.profile_photo_url,
                        } as any
                      }
                      size="xs"
                      avatar_image={user.profile_photo_url || undefined}
                      sx={{
                        border: '1.5px solid white',
                        width: '20px',
                        height: '20px',
                      }}
                    />
                  ))}
                </AvatarGroup>
                <TextG10
                  whiteSpace="nowrap"
                  color={theme.colors.blue}
                  _hover={{
                    textDecoration: 'underline',
                  }}>{`${usersInterested} Neighbor${usersInterested !== 1 ? 's' : ''} Interested`}</TextG10>
              </Flex>
            )}
          </Flex>
        </Box>
      </PremiumCard>
      <UsersListModal
        isOpen={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        title="Neighbors Interested"
        totalCount={usersInterested}
        fetchUsersHook={useFetchEventInterestedGoingUsers}
        entityId={event.id}
        chatRoute={`/event/${event.id}/chat`}
      />
    </Box>
  );
};

export default PremiumEventCard;
